/**
 * Modal for validating eligibility verification data via raw response
 * or portal screenshots.
 * @module ValidateEvModal
 */

import JsonView from "@uiw/react-json-view";
import { githubLightTheme } from "@uiw/react-json-view/githubLight";
import { useEffect, useMemo, useRef, useState } from "react";
import Zoom from "react-medium-image-zoom";
import "react-medium-image-zoom/dist/styles.css";
import { useSelector } from "react-redux";
import { Modal } from "risa-oasis-ui_v2";
import { getFileDownloadUrl } from "../../../api/firebase/firestoreService";
import { usePayerNameContextFromSingleOrderLayout } from "../../../context/payerNameContextFromSingleOrderLayout";
import {
  CoverageResult,
  Demographics,
  SingleCoverage,
} from "../../../data-model/medicalPaOrdersModel";
import {
  ModalNamesForBigQuery,
  ScreenNamesForBigQuery,
} from "../../../enums/eventsData";
import { EventsName } from "../../../enums/eventsName";
import { RootState } from "../../../redux/store/store";
import { getOrgIdForFetchExternalWorklist } from "../../../utils/organizationHelper";
import { apiWorkerManager } from "../../../workerManagers/workerManager";
import CustomRadioButton from "../../customRadioButton/customRadioButton";

/** Props for the {@link ValidateEvModal} component. */
interface ValidateEvModalProps {
  /** Callback to close the modal. */
  onClose: () => void;
  /** Unique identifier for the modal instance. */
  id: string;
  /** Index of the active coverage tab in display order (primary, then secondary, …). */
  activeTabIndex: number;
  /**
   * `coverage_results` row for the active tab, resolved with the same ordering
   * logic as the coverage tabs (see `getCoverageResultForCoverageRow`). When
   * set, this takes precedence over indexing `coverageResults` by tab index.
   */
  activeCoverageResult?: CoverageResult;
  /**
   * Raw `coverage_results` from the API. Used when `activeCoverageResult` is
   * not passed (legacy index-by-position behavior).
   */
  coverageResults: CoverageResult[];
  /**
   * Entire payload returned by the coverage endpoint
   * (`/medical/frontend-query/coverage`). Rendered as-is in the Raw Response
   * view so reviewers see the complete `coverages` + `coverage_results` result,
   * not just the active insurance tier.
   */
  fullCoverageResponse?: {
    coverages: SingleCoverage[];
    coverage_results: CoverageResult[];
  };
  /** PA order ID to validate coverage data for. */
  orderId: string;
  /** Patient demographics used for event logging. */
  demographics: Demographics;
}

/**
 * Modal for validating eligibility verification (EV) data.
 * Provides two view modes: raw JSON response and portal screenshots.
 * The raw JSON view shows the entire coverage endpoint payload
 * (`coverages` + `coverage_results`). Screenshot paths are read from the active
 * tab's `coverage_results` row and resolved to URLs from Firebase Storage.
 * Logs view-mode toggle events to BigQuery.
 *
 * @param props - {@link ValidateEvModalProps}
 */
const ValidateEvModal = (props: ValidateEvModalProps) => {
  const { user } = useSelector(
    (state: RootState) => state.firebaseAuthentication,
  );

  const { payerName } = usePayerNameContextFromSingleOrderLayout();

  const org_id = getOrgIdForFetchExternalWorklist();

  /**
   * Row for EV raw response / screenshots: prefer the tab-aligned result from
   * the parent so display order (primary → secondary) matches API rows even when
   * `coverage_results` array order differs.
   */
  const activeCoverageRow =
    props.activeCoverageResult ?? props.coverageResults?.[props.activeTabIndex];

  const [screenshotsUrl, setScreenshotsUrl] = useState<{
    accumulator: string;
    benefits: string;
  }>({
    accumulator: "",
    benefits: "",
  });
  const [screenshotsRef, setScreenshotsRef] = useState<{
    accumulator: string;
    benefits: string;
  }>({
    accumulator: "",
    benefits: "",
  });
  const [selectedOption, setSelectedOption] = useState<string>("raw_response");

  const isEventTriggerRef = useRef(false);

  const [isLoading, setIsLoading] = useState({
    accumulator: true,
    benefits: true,
  });

  /**
   * Resolves the EV raw-response bucket (`primary` / `secondary` / `tertiary`)
   * for the active insurance tab from the resolved coverage row and tab index.
   *
   * @returns Tier label (`primary` | `secondary` | `tertiary`) for analytics and JSON keys
   */
  const getCoverageType = (): string => {
    const row = activeCoverageRow;
    const fromRow = row?.coverage_type?.trim().toLowerCase();
    if (
      fromRow === "primary" ||
      fromRow === "secondary" ||
      fromRow === "tertiary"
    ) {
      return fromRow;
    }
    switch (props.activeTabIndex) {
      case 0:
        return "primary";
      case 1:
        return "secondary";
      case 2:
        return "tertiary";
      default:
        return "primary";
    }
  };

  const checkIfScreenshotsExist = useMemo(() => {
    return screenshotsRef.accumulator !== "" || screenshotsRef.benefits !== "";
  }, [screenshotsRef]);

  const filteredData = useMemo(() => {
    const response = props.fullCoverageResponse;
    const hasData =
      (response?.coverages?.length ?? 0) > 0 ||
      (response?.coverage_results?.length ?? 0) > 0;
    if (response && hasData) {
      return response;
    }
    return {
      message: "Could not find coverage data in the response.",
    };
  }, [props.fullCoverageResponse]);

  useEffect(() => {
    const row = activeCoverageRow;
    if (!row) {
      setScreenshotsRef({ accumulator: "", benefits: "" });
      return;
    }

    setIsLoading({ accumulator: true, benefits: true });

    const raw = row.raw_response;
    const rawObj =
      raw !== undefined &&
      raw !== null &&
      typeof raw === "object" &&
      !Array.isArray(raw)
        ? (raw as Record<string, unknown>)
        : {};
    const acccumulatorScreenshotRef =
      typeof rawObj.accumulator_screenshot === "string"
        ? rawObj.accumulator_screenshot
        : "";
    const benefits_screenshot_ref =
      typeof rawObj.benefits_screenshot === "string"
        ? rawObj.benefits_screenshot
        : "";

    setScreenshotsRef({
      accumulator: acccumulatorScreenshotRef,
      benefits: benefits_screenshot_ref,
    });

    if (!isEventTriggerRef.current) {
      apiWorkerManager.enqueueAndProcess({
        patient_id:
          props.demographics?.patient_id ??
          props.demographics?.patient_mrn ??
          "",
        order_id: props.orderId ?? "",
        org_id: org_id ?? "",
        user_id: user?.id ?? "",
        user_email: user?.email ?? "",
        event_name: EventsName.MODAL_SHOWN,
        additional_data: {
          screen_name: ScreenNamesForBigQuery.COVERAGE_SCREEN,
          modal_name: ModalNamesForBigQuery.VALIDATE_EV_BV_DATA_MODAL,
          insurance_type: getCoverageType(),
          screen_type: selectedOption,
          payer_name: payerName,
        },
      });

      apiWorkerManager.enqueueAndProcess({
        patient_id:
          props.demographics?.patient_id ??
          props.demographics?.patient_mrn ??
          "",
        order_id: props.orderId ?? "",
        org_id: org_id ?? "",
        user_id: user?.id ?? "",
        user_email: user?.email ?? "",
        event_name: EventsName.VALIDATE_EV_OPTION_SELECTED + " (Raw Response)",
        additional_data: {
          screen_name: ScreenNamesForBigQuery.COVERAGE_SCREEN,
          modal_type: ModalNamesForBigQuery.VALIDATE_EV_BV_DATA_MODAL,
          view_type: getCoverageType(),
          screen_type: "raw_response",
          payer_name: payerName,
        },
      });

      isEventTriggerRef.current = true;
    }
  }, [
    props.activeTabIndex,
    props.coverageResults,
    activeCoverageRow,
    props.demographics?.patient_id,
    props.demographics?.patient_mrn,
    props.orderId,
    org_id,
    user?.id,
    user?.email,
    payerName,
  ]);

  useEffect(() => {
    if (screenshotsRef.accumulator) {
      getFileDownloadUrl(screenshotsRef.accumulator).then((url) => {
        setScreenshotsUrl((prev) => ({ ...prev, accumulator: url }));
      });
    }
  }, [screenshotsRef.accumulator]);

  useEffect(() => {
    if (screenshotsRef.benefits) {
      getFileDownloadUrl(screenshotsRef.benefits).then((url) => {
        setScreenshotsUrl((prev) => ({ ...prev, benefits: url }));
      });
    }
  }, [screenshotsRef.benefits]);

  const handleLoad = (type: "accumulator" | "benefits") => {
    setIsLoading((prev) => ({ ...prev, [type]: false }));
  };

  const handleError = (type: "accumulator" | "benefits") => {
    setIsLoading((prev) => ({ ...prev, [type]: false }));
  };

  return (
    <Modal
      dialogId="validate-ev-modal"
      title={`Validate EV Data`}
      saveButtonText="Save"
      cancelText="Cancel"
      onSave={() => {}}
      heightPercentage={70}
      onClose={() => {
        props.onClose();
        apiWorkerManager.enqueueAndProcess({
          patient_id:
            props.demographics?.patient_id ??
            props.demographics?.patient_mrn ??
            "",
          order_id: props.orderId ?? "",
          org_id: org_id ?? "",
          user_id: user?.id ?? "",
          user_email: user?.email ?? "",
          event_name: EventsName.CLOSE_BUTTON_CLICKED,
          additional_data: {
            screen_name: ScreenNamesForBigQuery.COVERAGE_SCREEN,
            patient_name:
              props.demographics?.first_name +
              " " +
              props.demographics?.last_name,
            source: "validate_ev_box",
            payer_name: payerName,
          },
        });
      }}
      hideFooter={true}
    >
      <div className="cmm-input--viewer flex h-full flex-col gap-2 rounded-md">
        <div className="cmm-input--viewer options flex items-center gap-4">
          <div className="cmm-input--viewer all-options flex items-center gap-2">
            <CustomRadioButton
              label=" View Raw Response"
              value="raw_response"
              onChange={() => {
                setSelectedOption("raw_response");

                apiWorkerManager.enqueueAndProcess({
                  patient_id:
                    props.demographics?.patient_id ??
                    props.demographics?.patient_mrn ??
                    "",
                  order_id: props.orderId ?? "",
                  org_id: org_id ?? "",
                  user_id: user?.id ?? "",
                  user_email: user?.email ?? "",
                  event_name:
                    EventsName.VALIDATE_EV_OPTION_SELECTED + " (Raw Response)",
                  additional_data: {
                    screen_name: ScreenNamesForBigQuery.COVERAGE_SCREEN,
                    modal_type: ModalNamesForBigQuery.VALIDATE_EV_BV_DATA_MODAL,
                    view_type: getCoverageType(),
                    screen_type: "raw_response",
                    payer_name: payerName,
                  },
                });
              }}
              checked={selectedOption === "raw_response"}
              name={"options"}
              className="mb-0"
            />
            <CustomRadioButton
              label=" View Screenshots"
              value="screenshots"
              onChange={() => {
                setSelectedOption("screenshots");
                apiWorkerManager.enqueueAndProcess({
                  patient_id:
                    props.demographics?.patient_id ??
                    props.demographics?.patient_mrn ??
                    "",
                  order_id: props.orderId ?? "",
                  org_id: org_id ?? "",
                  user_id: user?.id ?? "",
                  user_email: user?.email ?? "",
                  event_name:
                    EventsName.VALIDATE_EV_OPTION_SELECTED + " (Screenshots)",
                  additional_data: {
                    screen_name: ScreenNamesForBigQuery.COVERAGE_SCREEN,
                    modal_type: ModalNamesForBigQuery.VALIDATE_EV_BV_DATA_MODAL,
                    view_type: getCoverageType(),
                    screen_type: "screenshots",
                    payer_name: payerName,
                  },
                });
              }}
              checked={selectedOption === "screenshots"}
              name={"options"}
              className="mb-0"
            />
          </div>
        </div>
        <div className="cmm-input--viewer mt-4 h-full rounded-md">
          {selectedOption === "raw_response" && (
            <JsonView
              style={githubLightTheme}
              value={filteredData || { message: "Loading data..." }}
              displayDataTypes={false}
            />
          )}
          {selectedOption === "screenshots" && (
            <>
              {checkIfScreenshotsExist ? (
                <div className="cmm-input--viewer flex flex-col gap-4">
                  {screenshotsUrl.accumulator !== "" && (
                    <>
                      {isLoading.accumulator && (
                        <div className="absolute inset-0 flex items-center justify-center">
                          <div className="h-12 w-12 animate-spin rounded-full border-b-2 border-t-2 border-gray-900"></div>
                        </div>
                      )}
                      <Zoom>
                        <img
                          src={screenshotsUrl.accumulator ?? ""}
                          alt="Accumulator Page"
                          onLoad={() => handleLoad("accumulator")}
                          onError={() => handleError("accumulator")}
                          className={isLoading.accumulator ? "hidden" : "block"}
                        />
                      </Zoom>
                    </>
                  )}
                  {screenshotsUrl.benefits !== "" && (
                    <>
                      {isLoading.benefits && (
                        <>
                          <div className="absolute inset-0 flex items-center justify-center">
                            <div className="h-12 w-12 animate-spin rounded-full border-b-2 border-t-2 border-gray-900"></div>
                          </div>
                        </>
                      )}
                      <Zoom>
                        <img
                          src={screenshotsUrl.benefits ?? ""}
                          alt="Benefits Page"
                          onLoad={() => handleLoad("benefits")}
                          onError={() => handleError("benefits")}
                          className={isLoading.benefits ? "hidden" : "block"}
                        />
                      </Zoom>
                    </>
                  )}
                </div>
              ) : (
                <div className="cmm-input--viewer flex h-full flex-col gap-4">
                  <div className="cmm-input--viewer mt-8 flex h-full items-start justify-center text-sm text-primaryGray-5">
                    No screenshots found for this coverage type.
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </Modal>
  );
};

export default ValidateEvModal;
